/**
 * 채널(플랫폼) 표시 정보와 게시물 유형 판별.
 *
 * ⚠️ 읽는 카탈로그는 `src/constants/platform-catalog` 다. 루트의 `src/utils/platforms.ts`(소비처 17곳)와는
 * **다른 파일**이다 — 둘을 같은 것으로 보고 고치면 안 된다.
 */
import type { SocialPostInput } from 'src/utils/moments/domain/social-post-input';

import platforms from 'src/constants/platform-catalog';

/**
 * 게시물 타입을 판별
 * @param {Object} post - 게시물 객체
 * @returns {string} 게시물 타입
 * 
 * @example
 * getPostType({ title: "제목", content: "내용", imageUrl: "image.jpg" }) // "full"
 */
export const getPostType = (post: SocialPostInput | null | undefined): string => {
  if (!post) return 'unknown';

  const hasTitle = post.title && post.title.trim().length > 0;
  const hasContent = post.content && post.content.trim().length > 0;
  const hasImage = post.imageUrl || post.imageS3Url;

  if (hasImage && hasTitle && hasContent) return 'full';
  if (hasImage && hasTitle && !hasContent) return 'image_title';
  if (hasImage && !hasTitle && !hasContent) return 'image_only';
  if (!hasImage && hasTitle && hasContent) return 'text_only';
  if (!hasImage && hasTitle && !hasContent) return 'title_only';

  return 'unknown';
};

/**
 * 플랫폼 정보 객체 생성
 * @param {string} platform - 플랫폼 이름 또는 id
 * @returns {Object} 플랫폼 정보 객체
 * 
 * @example
 * getPlatformInfo('instagram') // { label: 'Instagram', color: '#E4405F', textColor: '#fff' }
 */
export const getPlatformInfo = (platform: string | null | undefined) => {
  if (!platform) return { label: 'Unknown', color: '#666666', textColor: '#fff' };
  const key = String(platform || '').toLowerCase();
  const found = platforms.find(
    (p) => p.id?.toLowerCase() === key || p.label?.toLowerCase() === key
  );
  if (found) {
    // ⚠️ `...found`가 뒤에 오므로, found에 **해당 키가 존재하면** 위 폴백은 덮인다.
    // (found.label이 undefined로 존재하면 label도 undefined가 된다 — 폴백이 무력화)
    // TS가 TS2783으로 잡아낸 지점이지만, 이 커밋은 타입만 올리므로 동작은 그대로 둔다.
    // 현재 platforms 데이터는 label·color·textColor가 모두 채워져 있어 겉으로 드러나지 않는다.
    const fallback = {
      label: found.label || found.id || 'Unknown',
      color: found.color || '#666666',
      textColor: found.textColor || '#fff',
      icon: found.icon,
    };
    return { ...fallback, ...found };
  }
  return { label: platform, color: '#666666', textColor: '#fff' };
};
